/* eslint-disable class-methods-use-this */
import CollectionApi from 'client/api/v2/CollectionApi';
import axios from 'axios';

const apiRoute = '/api/v2/providers';

class ProvidersApi extends CollectionApi {
  constructor(providerId = '') {
    super(`${apiRoute}/${providerId}/administrators`, {
      limit: 50,
      skip: 0,
      sort: 'email', // Prepend with a '-' to reverse default sort direction
    });

    // Keep track of the Provider Id to assist with catching errors
    this.providerId = providerId;
  }

  /**
   * HACK: Add displayName to response.results to avoid having to use a component to display it in
   * the <ItemTable>.
   */
  async fetch(skip, limit, sort, searchTerm, cancelToken, filters = [], pageToken, skipToken) {
    const filtersWithSearch = [
      ...filters,
    ];
    const response = await super.fetch(
      skip, limit, sort, searchTerm, cancelToken, filtersWithSearch, pageToken, skipToken,
    );
    const results = response.results.map((result) => {
      const { firstname, lastname } = result;
      const displayName = firstname || lastname ? `${firstname} ${lastname}` : '--';
      return { ...result, displayName };
    });

    return { results, totalCount: response.totalCount };
  }

  /**
   * @param {string} providerId - Provider ID
   * @param {string} administratorId - administrator ID
   */
  async deleteAdministrator(providerId, administratorId) {
    return super.initialize()
      .then(() => axios.delete(`${apiRoute}/${providerId}/administrators/${administratorId}`));
  }

  /**
   * @param {string} providerId - Provider ID
   * @param {string} administratorId - administrator ID
   */
  async getAdministrator(providerId, administratorId) {
    const response = await axios.get(`${apiRoute}/${providerId}/administrators?filter=id:eq:${administratorId}`);
    return response.data;
  }

  /**
   * @description: get all the organizations associated with this provider
   * @param {string} providerId - the provider ID
   * @param {object} options - the options to filter/skip/limit the organizations
   */
  async getOrganizations(providerId, options = {}) {
    const {
      skip = 0,
      limit = 0,
      searchTerm,
      searchByFields = ['settings.name'],
      sortBy = 'settings.name',
      fields = ['id', 'settings.name'],
    } = options;
    const param = this.param({
      skip,
      limit,
      sort: sortBy,
      filter: searchTerm ? searchByFields.map((field) => `${field}:search:${searchTerm}`).join(',') : '',
      fields,
    });
    const response = await axios.get(`${apiRoute}/${providerId}/organizations?${param}`);
    return response.data;
  }

  /**
   * @param {string} id - Provider id
   */
  getPaymentDetails(id) {
    return this.initialize()
      .then(() => axios.get(`${apiRoute}/${id}/paymentdetails`).then((resp) => resp.data));
  }

  /**
   * @param {string} id - Provider id
   */
  getPaymentParameters(id) {
    return this.initialize()
      .then(() => axios.get(`${apiRoute}/${id}/paymentparams`).then((resp) => resp.data));
  }

  /**
   * @param {string} providerId - ProviderID
   */
  async getProviderDetails(providerId) {
    const response = await axios.get(`${apiRoute}/${providerId}`);
    return response.data;
  }

  setProviderId(providerId = '') {
    this.baseUrl = `${apiRoute}/${providerId}/administrators`;
    this.providerId = providerId;
  }

  /**
   * @param {string} id - Provider id
   * @param {Object} payload - JSON Object containing payment details to update
   */
  updatePaymentDetails(id, payload) {
    return this.initialize()
      .then(() => axios.put(`${apiRoute}/${id}/paymentdetails`, payload));
  }

  /**
   * @param {string} id - Provider id
   * @param {Object} payload - JSON Object containing command template details to create
   */
  createCommandTemplate(id, payload) {
    return this.initialize()
      .then(() => axios.post(`${apiRoute}/${id}/commandtemplates`, payload));
  }

  getCommandTemplate(providerId, idCommandtemplate) {
    return this.initialize()
      .then(() => axios.get(`${apiRoute}/${providerId}/commandtemplates/${idCommandtemplate}`));
  }

  createPolicyGroupTemplate(providerId, payload) {
    return this.initialize()
      .then(() => axios.post(`${apiRoute}/${providerId}/policyGroupTemplates`, payload));
  }

  async convertPolicies(providerId, policyId) {
    const payload = {
      policyId,
    };

    return this.initialize()
      .then(() => axios.post(`${apiRoute}/${providerId}/configuredpolicytemplates/frompolicy`, payload));
  }
}

export default new ProvidersApi();
